// Entités VIRTUELLES du sélecteur d'entités — décision William du 02/10/2026 (option B).
//
// Deux vues de LECTURE sans fiche société derrière :
// - « Particuliers (sans société) » : tout ce qui n'a pas de société (`null`, `""` ou absent) ;
//   pour les tickets, hors tickets labo (`project_work`) ;
// - « Professionnels » : société renseignée, HORS METERIS · MCF · SCI (sociétés archivées
//   comprises) ; pour les tickets, hors tickets globaux.
//
// La définition vit côté SERVEUR (`backend/app/services/entite_virtuelle_svc.py`) : ce module ne
// fait que traduire l'entité choisie — en paramètres pour les endpoints tickets, ou en
// appartenance pour un écran qui filtre une liste déjà chargée. Les organisations internes se
// lisent sur le champ `interne` de l'arbre `/api/tk/admin/clients/tree` : leurs identifiants ne
// sont JAMAIS recopiés ici.
//
// Les sentinelles sont un contrat avec le serveur (`PARTICULIER_CLIENT_SENTINEL`,
// `PROFESSIONNEL_CLIENT_SENTINEL`) : ne pas les changer d'un seul côté.

export const PARTICULIER_ENTITY_ID = '__particuliers__';
export const PROFESSIONNEL_ENTITY_ID = '__professionnels__';

export const PARTICULIER_ENTITY = Object.freeze({
  id: PARTICULIER_ENTITY_ID,
  name: 'Particuliers (sans société)',
  sousTitre: "entité virtuelle — tout ce qui n'a pas de société",
  virtual: true,
  clientType: 'particulier',
});

export const PROFESSIONNEL_ENTITY = Object.freeze({
  id: PROFESSIONNEL_ENTITY_ID,
  name: 'Professionnels',
  sousTitre: 'clients avec société, hors METERIS · MCF · SCI',
  virtual: true,
  clientType: 'pro',
});

// Ordre d'affichage dans le sélecteur.
export const ENTITES_VIRTUELLES = Object.freeze([PARTICULIER_ENTITY, PROFESSIONNEL_ENTITY]);

const PAR_ID = Object.freeze(Object.fromEntries(ENTITES_VIRTUELLES.map((e) => [e.id, e])));

function idDe(entity) {
  if (!entity) return '';
  return typeof entity === 'string' ? entity : (entity.id || '');
}

/** L'entité virtuelle de cet identifiant (ou de cette entité), sinon `null`. */
export function entiteVirtuelle(entity) {
  return PAR_ID[idDe(entity)] || null;
}

/** Vrai pour « Particuliers » et « Professionnels » — jugé sur l'IDENTIFIANT, pas sur un drapeau. */
export function estVirtuelle(entity) {
  return Boolean(entiteVirtuelle(entity));
}

export function estParticuliers(entity) {
  return idDe(entity) === PARTICULIER_ENTITY_ID;
}

export function estProfessionnels(entity) {
  return idDe(entity) === PROFESSIONNEL_ENTITY_ID;
}

/**
 * L'entité si c'est une VRAIE société, sinon `null`. Pour un écran qui ne sait pas traduire une
 * entité virtuelle : il l'ignore (et le dit : `<EntityFilterBanner virtuelleIndisponible />`).
 */
export function societeReelle(entity) {
  return entity && entity.id && !estVirtuelle(entity) ? entity : null;
}

/**
 * Entité relue du stockage : une entité virtuelle reprend le nom et le sous-titre de la
 * constante (un ancien libellé enregistré ne survit pas à un renommage) ; une vraie société
 * est rendue telle quelle.
 */
export function rafraichirEntite(entity) {
  if (!entity) return entity ?? null;
  return entiteVirtuelle(entity) || entity;
}

/**
 * Paramètres des endpoints TICKETS (liste, compteurs, tâches, frise) pour l'entité choisie,
 * cumulés au bouton Pro / Particulier / AMC / Global (`clientType`) :
 * - vraie société → `client_id` ;
 * - entité virtuelle → `client_type` (`pro` / `particulier`) ;
 * - entité virtuelle ET un autre bouton → le bouton en `client_type` et l'entité en `client_id`
 *   (sentinelle) : le serveur traduit la sentinelle et fait un ET, l'un n'écrase jamais l'autre.
 */
export function paramsTickets(entity, clientType = '') {
  const params = {};
  const virtuelle = entiteVirtuelle(entity);
  if (virtuelle) {
    if (!clientType || clientType === virtuelle.clientType) {
      params.client_type = virtuelle.clientType;
    } else {
      params.client_type = clientType;
      params.client_id = virtuelle.id;
    }
    return params;
  }
  if (idDe(entity)) params.client_id = idDe(entity);
  if (clientType) params.client_type = clientType;
  return params;
}

// Boutons « Type client » de la liste des tickets (components/ClientTypeSelector) qu'une entité
// virtuelle EXCLUT : le serveur fait un ET, l'intersection est vide par définition —
// « Professionnels » (société, hors globaux) ⟂ Particulier, Global ; « Particuliers » (sans
// société, hors globaux) ⟂ Pro, AMC (suppose une société sous contrat), Global.
const LIBELLE_TYPE_CLIENT = Object.freeze({ pro: 'Pro', particulier: 'Particulier', amc: 'AMC', global: 'Global' });
const TYPES_CLIENT_EXCLUS = Object.freeze({
  pro: Object.freeze(['particulier', 'global']),
  particulier: Object.freeze(['pro', 'amc', 'global']),
});

/**
 * Entité virtuelle et bouton « Type client » qui s'excluent : la phrase à afficher (une liste
 * vide sans explication passe pour une panne — relecture du 03/10/2026), sinon `null`.
 */
export function exclusionTypeClient(entity, clientType = '') {
  const virtuelle = entiteVirtuelle(entity);
  if (!virtuelle || !clientType) return null;
  if (!(TYPES_CLIENT_EXCLUS[virtuelle.clientType] || []).includes(clientType)) return null;
  const bouton = LIBELLE_TYPE_CLIENT[clientType] || clientType;
  return `« ${virtuelle.name} » et le filtre « ${bouton} » s’excluent : aucun ticket ne peut correspondre.`;
}

/**
 * Un filtre LOCAL sur l'entité peut-il s'appliquer ? « Professionnels » lit le champ `interne` de
 * l'arbre : tant que l'arbre n'est pas `pret`, METERIS / MCF / SCI passeraient pour des clients
 * (relecture du 03/10/2026). Rend `pret`, `chargement` ou `echec` ; toute autre entité (vraie
 * société, « Particuliers », aucune) ne dépend pas de l'arbre : `pret`. Un état inconnu vaut
 * `chargement` — jamais une liste fausse par défaut.
 */
export function etatArbrePourEntite(entity, etatArbre) {
  if (!estProfessionnels(entity)) return 'pret';
  return etatArbre === 'pret' || etatArbre === 'echec' ? etatArbre : 'chargement';
}

/** Message d'un écran dont le filtre local attend l'arbre (`etatArbrePourEntite`), sinon `null`. */
export function messageArbre(etat) {
  if (etat === 'chargement') return 'Chargement de la liste des sociétés…';
  if (etat === 'echec') {
    return 'La liste des sociétés n’a pas pu être chargée : impossible de distinguer les « Professionnels » '
      + 'de METERIS · MCF · SCI. Rechargez la page, ou choisissez une société.';
  }
  return null;
}

/**
 * Filtre LOCAL d'éléments rattachés à une société par un LIEN ENREGISTRÉ (tenant Acronis, compte ALSO, compte
 * téléphonique…) — jamais par le nom (30/10/2026). `societeDe(e)` rend la société LIÉE de l'élément (vide = aucun
 * lien) ; défaut : `e.client_id`.
 * - vraie société → ses éléments seuls ;
 * - « Professionnels » → les éléments liés à une société hors METERIS · MCF · SCI (attend l'arbre :
 *   `etatArbrePourEntite`) ;
 * - « Particuliers » → `null` : NON TRADUISIBLE — un élément sans lien n'est pas pour autant celui d'un
 *   particulier (l'écran le dit : `<EntityFilterBanner virtuelleIndisponible />`).
 * Rend `{ lignes, sansLien }` : `sansLien` = éléments écartés faute de lien, à dire à l'écran (ils
 * n'apparaissent sous aucune société). Sans entité : tout, `sansLien` = 0.
 */
export function filtrerParLien(elements, entity, societesParId = {}, societeDe = (e) => e?.client_id) {
  const liste = elements || [];
  if (!idDe(entity)) return { lignes: liste, sansLien: 0 };
  if (estParticuliers(entity)) return null;
  const lignes = [];
  let sansLien = 0;
  liste.forEach((e) => {
    const societe = societeDe(e);
    if (!societe) { sansLien += 1; return; }
    if (appartientAEntite(societe, entity, societesParId)) lignes.push(e);
  });
  return { lignes, sansLien };
}

/**
 * Filtre LOCAL : la société `societeId` appartient-elle à l'entité choisie ?
 * `societesParId` : fiches de l'arbre par identifiant (porte `interne`). Une société absente
 * de l'arbre (archivée) n'est pas interne : un ancien client reste un professionnel.
 */
export function appartientAEntite(societeId, entity, societesParId = {}) {
  const id = idDe(entity);
  if (!id) return true;
  const sansSociete = !societeId;
  if (id === PARTICULIER_ENTITY_ID) return sansSociete;
  if (id === PROFESSIONNEL_ENTITY_ID) {
    return !sansSociete && !(societesParId && societesParId[societeId]?.interne === true);
  }
  return !sansSociete && societeId === id;
}
